// Tiny fetch wrapper. Centralises:
//   - the base URL (VITE_API_BASE, then the documented VITE_API_BASE_URL alias;
//     local development defaults to :8081, while a no-env production build
//     uses the same-origin proxy),
//   - JSON Accept header,
//   - structured-error parsing (the API returns {error, message} on
//     non-2xx so we surface a typed `ApiError`).
//
// Deliberately no retry / no cache / no auth — we'll add them when
// some endpoint actually needs them. Today PR1.2 just reads OHLCV.

import { noteApiErrorCode } from '../lib/siteVersion'
import type { ApiErrorBody } from './types'

function resolveApiBase(): string {
  const fromEnv =
    (import.meta.env.VITE_API_BASE as string | undefined) ??
    (import.meta.env.VITE_API_BASE_URL as string | undefined)
  if (fromEnv != null) return fromEnv
  if (!import.meta.env.DEV) return ''
  // 127.0.0.1:5173 → localhost:8081 is another origin; CORS/fetch dies and
  // the pair page is a banner, not a page (фидбек 2026-08-27).
  if (typeof window !== 'undefined' && window.location.hostname === '127.0.0.1') {
    return 'http://127.0.0.1:8081'
  }
  return 'http://localhost:8081'
}

const API_BASE: string = resolveApiBase()

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly ticket?: string
  /** The error's whole JSON body, for a route whose error carries more than
   *  its code (a claim already in progress names its payout: `payout_id`). */
  readonly body?: Readonly<Record<string, unknown>>

  constructor(
    status: number,
    code: string,
    message: string,
    ticket?: string,
    body?: Readonly<Record<string, unknown>>
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.ticket = ticket
    this.body = body
  }
}

// An ApiError must never carry an empty message: ErrorBanner renders it
// verbatim, and a blank string produces a bare "⚠" with no text at all.
// Both fallbacks are reachable in production — over HTTP/2 `statusText` is
// always "" by spec, and a proxy error page (nginx 502/504) is not JSON, so
// neither the body message nor the status text is usable. Fall through to
// the status code, which is always present.
function errorMessage(body: Partial<ApiErrorBody>, resp: Response): string {
  return body.message?.trim() || resp.statusText.trim() || `HTTP ${resp.status}`
}

export async function apiFetch<T>(path: string, opts: RequestInit = {}): Promise<T> {
  const headers = new Headers(opts.headers)
  if (!headers.has('Accept')) headers.set('Accept', 'application/json')
  if (!headers.has('X-Wils-Requested-With')) headers.set('X-Wils-Requested-With', 'wils')
  const resp = await fetch(`${API_BASE}${path}`, {
    ...opts,
    credentials: 'include',
    headers,
  })
  if (!resp.ok) {
    const body = (await resp.json().catch(() => ({}) as ApiErrorBody)) as Partial<ApiErrorBody>
    if (body.error) noteApiErrorCode(body.error)
    throw new ApiError(
      resp.status,
      body.error ?? 'unknown',
      errorMessage(body, resp),
      body.ticket,
      body as Record<string, unknown>
    )
  }
  return (await resp.json()) as T
}

// apiBase is exported so debug overlays / settings page (PR4.6) can
// show which API the UI is actually talking to.
export const apiBase = API_BASE
